/**
 * Public dancer profile: /u/$handle
 *
 * Deliberately outside the `_authenticated` tree. A dancer profile is meant to
 * be linkable from a review or a community post, so it has to render for an
 * anonymous visitor and for a crawler. Everything it displays comes from the
 * backend whitelist; nothing here reads session state.
 */
import { createFileRoute, notFound } from '@tanstack/react-router';
import { ErrorState } from '@/components/v2/error-state';
import { PublicProfileTemplatePending } from '@/components/v2/page-template-pending';
import { useSmartBack } from '@/lib/navigation/smartBack';
import { v2 } from '@/lib/i18n/v2';
import { getPublicProfile, type PublicDancerProfile } from '@/lib/api/v2/public-profile';
import { entityHeadMeta } from '@/lib/utils/site-meta';

export const Route = createFileRoute('/u/$handle')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The page is a header-less centered identity card — a back-header list
  // pending read as the wrong destination entirely.
  pendingComponent: PublicProfileTemplatePending,
  loader: async ({ params }) => {
    const result = await getPublicProfile(params.handle);
    if (!result.ok) {
      // A missing, suspended, deleted or staff handle is indistinguishable by
      // design, so it is a 404 rather than a 403.
      throw notFound();
    }
    return { profile: result.value };
  },
  head: ({ loaderData, params }) => {
    const profile = loaderData?.profile;
    const name = profile?.displayName ?? params.handle;
    return {
      ...entityHeadMeta({
        title: `${name} - Dancer | TIL`,
        description: `See ${name} on TIL, Singapore's dance class directory.`,
        path: `/u/${profile?.handle ?? params.handle}`,
      }),
    };
  },
  component: PublicProfilePage,
  notFoundComponent: ProfileNotFound,
});

function PublicProfilePage() {
  const { profile } = Route.useLoaderData() as { profile: PublicDancerProfile };
  const name = profile.displayName ?? profile.handle;
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col items-center gap-4 px-6 py-12">
      <div
        aria-hidden="true"
        className="flex h-24 w-24 items-center justify-center rounded-full bg-neutral-200 text-3xl font-semibold text-neutral-700"
      >
        {initial}
      </div>
      <h1 className="text-2xl font-semibold">{name}</h1>
      <p className="text-sm text-neutral-500">@{profile.handle}</p>
    </main>
  );
}

function ProfileNotFound() {
  // Pop history when reached in-app; cold-opened dead handles have no in-app
  // history, and a bare window.history.back() would exit the site — replace
  // to discovery instead.
  const smartBackToDiscovery = useSmartBack({ to: '/discovery' });
  return (
    <ErrorState
      severity="not-found"
      page
      title={v2.error.notFound('this profile')}
      onGoBack={smartBackToDiscovery}
      onHome={() => window.location.assign('/')}
    />
  );
}
